/* @flow */

import type { CONFIRMATION_DATA_MODAL_TYPE } from '../../components/modal/confirmationModal/ConfirmationModal';
import type { BasicFunction } from '../jsHelpers/types';
import type { GamestreamApiResponse, GamestreamGameConfig, GamestreamSession, Profile, Profiles, PromptSessionCloseType, ShowEulaType, ShowGameProfileSelectType } from './types';

import { ConfirmationModalResult } from '../../components/modal/confirmationModal/ConfirmationModal';
import { EulaModalResult } from '../../components/modal/eulaModal/EulaModal';
import { HttpStatus } from '../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { logDebug, logError, logInfo, logWarning } from '../debug/debug';
import { fireAndForget } from '../jsHelpers/promise';
import Localizer from '../localization';
import { Messenger, MessengerEvents } from '../messenger';
import GameManager from './gameManager';
import GamestreamApi from './gamestreamApi';
import { buildSessionPayload, extractNickname } from './helpers';
import Player from './player.js';
import { GamestreamStatus } from './types';

class GamestreamWrapper {
  avatarBaseUrl: string;
  cgxClient: CGXClient | null;
  gameAlias: string | null;
  onStop: BasicFunction;
  onError: (error: GamestreamApiResponse) => void;
  profileId: string | null;
  profiles: Profiles;
  sessionTracker: SGXSessionTracker | null;
  showEula: ShowEulaType;
  promptSessionClose: PromptSessionCloseType;
  updatePlayingHistoryItem: (profileId: string) => Promise<void>;

  constructor(
    backendUrl: string,
    avatarBaseUrl: string,
    authToken: string,
    onStop: BasicFunction,
    onError: (error: GamestreamApiResponse) => void,
    showEula: ShowEulaType,
    promptSessionClose: PromptSessionCloseType,
    updatePlayingHistoryItem: (profileId: string) => Promise<void>,
  ) {
    this.avatarBaseUrl = avatarBaseUrl;
    this.cgxClient = null;
    this.gameAlias = null;
    this.onStop = onStop;
    this.onError = onError;
    this.profileId = null;
    this.promptSessionClose = promptSessionClose;
    this.sessionTracker = null;
    this.showEula = showEula;
    this.updatePlayingHistoryItem = updatePlayingHistoryItem;

    GamestreamApi.backendUrl = backendUrl;
    GamestreamApi.authToken = authToken;
  }

  handleOnBeforeUnload = () => {
    this.stopGame();
  };

  stopGame() {
    window.removeEventListener('beforeunload', this.handleOnBeforeUnload, { passive: true });

    this.gameAlias = null;

    if (this.cgxClient) {
      // Stop client
      this.cgxClient.Stop();
      this.cgxClient = null;

      try {
        GameManager.CGXClientClass.resizeHandler = null;
      } catch (error) {
        logWarning(`Failed to clear resizeHandler: ${error.message}`);
      }
    }

    if (this.sessionTracker) {
      // Stop session tracker
      this.sessionTracker.terminate();
      this.sessionTracker = null;
    }
  }

  exit() {
    this.stopGame();
    this.onStop();
  }

  handleConnected() {
    // Nothing to do at the moment
  }

  handleDisconnected(gsWrapper: GamestreamWrapper) {
    gsWrapper.onStop();
  }

  handleError(errorMsg: string) {
    Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('gaming.errors.client_error', { errorMsg }));
  }

  async initialize(gameAlias: string, showGameProfileSelect: ShowGameProfileSelectType, preselectedProfileId: string | null): Promise<void> {
    const profiles = await GamestreamApi.getProfiles();

    if (!profiles || profiles.length === 0) {
      return Promise.reject(new Error(Localizer.localize('gaming.errors.missing_profiles')));
    }

    this.gameAlias = gameAlias;
    this.profiles = profiles;

    Messenger.once(MessengerEvents.MODAL_CLOSED, this.gameProfileSelectClosedCallback);

    return showGameProfileSelect({
      preselectedProfileId,
      profiles: profiles.map(({ avatar_filename: avatarUrl, nickname, uid: profileId }) => {
        return { avatarUrl: `${this.avatarBaseUrl}${avatarUrl}`, nickname: extractNickname(nickname), profileId };
      }),
    });
  }

  gameProfileSelectClosedCallback = (profileId: string | null): Promise<void> => {
    if (profileId === undefined || profileId === null) {
      // Game launch canceled
      this.exit();
      return Promise.resolve();
    }

    this.profileId = profileId;

    return this.startSession();
  };

  async startSession(): Promise<void> {
    if (this.gameAlias === null) {
      logError('Missing game alias');
      return;
    }

    if (this.profileId === null) {
      logError('Missing profile Id');
      return;
    }

    const session = await GamestreamApi.startSession(buildSessionPayload(this.gameAlias, this.profileId));
    if (!session) {
      Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('gaming.errors.start_session'));
      return;
    }

    if (session.sessionId === undefined) {
      // Handle GS error (this "if" is there to refine session to the GamestreamApiResponse type)

      const { gsStatusCode, status } = session;

      if (status === HttpStatus.NotAcceptable && gsStatusCode === GamestreamStatus.MustApproveEula) {
        // Not exactly an error to be reported to user
        if (!(await this.getAndApproveEula())) {
          // User must approve or decline EULA
          return;
        }
      }

      if (gsStatusCode === GamestreamStatus.AlreadyPlaying) {
        // Another session already exists: ask user if they want to close it
        return this.promptUserToCloseSession(session);
      }

      // All other errors will be processed in GameManager
      this.onError(session);
      return;
    }

    // WARNING: when continuing a session, 'useSsl' is not returned
    const { sessionId } = session;

    const missingSessionIdError: GamestreamApiResponse = { error: new Error('Missing session Id'), response: null };

    if (!sessionId) {
      this.onError(missingSessionIdError);
      return;
    }

    // EULA already approved or auto-approved
    const gameConfig = await GamestreamApi.getGameConfig(sessionId);

    if (gameConfig.response) {
      // Error detected: will be processed in GameManager
      this.onError(gameConfig);
      return;
    }

    if (!session.sessionId) {
      this.onError(missingSessionIdError);
      return;
    }

    this.runGame(session, gameConfig);
  }

  async getAndApproveEula(): Promise<boolean> {
    const { gameAlias } = this;

    if (gameAlias === null) {
      logError('Missing game alias');
      return false;
    }

    logInfo('Player must approve EULA before playing');
    logInfo('Retrieving EULA...');
    const eulaResponse = await GamestreamApi.getEula(gameAlias);

    Messenger.once(MessengerEvents.MODAL_CLOSED, this.eulaClosedCallback);

    if (!eulaResponse || !eulaResponse.eula) {
      // No EULA: start session
      logInfo('No EULA. Auto accepting');
      await this.acceptEula();
      return true;
    }

    this.showEula({ text: eulaResponse.eula });
    logInfo('Waiting for user to accept/decline EULA...');
    return false;
  }

  eulaClosedCallback = async (result: EulaModalResult): Promise<void> => {
    if (result === EulaModalResult.Accept) {
      await this.acceptEula();
      return this.startSession();
    }
  };

  async acceptEula() {
    const { gameAlias } = this;

    if (gameAlias === null) {
      logError('Missing game alias');
      return;
    }

    logInfo('Approving EULA...');
    await GamestreamApi.postEula(gameAlias);
    logInfo('EULA approved');
  }

  async findPlayingProfile(): Promise<Profile | null> {
    const { profiles } = this;

    for (const profile of profiles) {
      const session = await GamestreamApi.getSession(profile.uid);
      if (session !== null) {
        return profile;
      }
    }

    // Playing profile not found
    return null;
  }

  async promptUserToCloseSession(session: GamestreamApiResponse): Promise<void> {
    const playingProfile = await this.findPlayingProfile();

    if (playingProfile === null) {
      // Playing profile not found: display regular "session in progress" error
      this.onError(session);
      return;
    }

    const data: CONFIRMATION_DATA_MODAL_TYPE = {
      button1Title: Localizer.localize('common.actions.cancel'),
      button2Title: Localizer.localize('gaming.actions.continue'),
      header: Localizer.localize('gaming.session_close.title'),
      question: Localizer.localize('gaming.session_close.question', { profileName: extractNickname(playingProfile.nickname) }),
    };

    Messenger.once(MessengerEvents.MODAL_CLOSED, (result: ConfirmationModalResult) => this.promptUserToCloseSessionCallback(result, playingProfile.uid));
    this.promptSessionClose(data, Localizer.localize('accessibility.modal.confirmation_close_session'));
  }

  promptUserToCloseSessionCallback: (result: ConfirmationModalResult, profileId: string) => Promise<void> = (result, profileId) => {
    if (result !== ConfirmationModalResult.Button2) {
      // User chose not to close session
      this.exit();
      return Promise.resolve();
    }

    return this.closeSession(profileId);
  };

  async closeSession(profileId: string): Promise<void> {
    if (!(await GamestreamApi.stopSession(profileId))) {
      this.onError({ error: new Error('Cannot stop session'), message: Localizer.localize('gaming.session_close.error'), response: null });
      return Promise.resolve();
    }

    // Session successfully closed: restart session
    return this.startSession();
  }

  startSessionTracker(session: GamestreamSession) {
    const { ipTracker, sessionId, useSsl } = session;

    const SGXSessionTracker = GameManager.SGXSessionTrackerClass;

    const tracker = new SGXSessionTracker({
      reconnect_attempts: 5,
      server: ipTracker,
      session_id: sessionId,
      ssl: useSsl ?? true,
    });

    tracker.on('debug', (debugString: string) => {
      logDebug(`[SessionTracker] Debug: ${debugString}`);
    });

    tracker.on('connected', () => {
      logInfo('[SessionTracker] Tracker connected');
    });

    tracker.on('connection_failed', (remaining_attempts: number) => {
      if (remaining_attempts > 0) {
        logInfo(`[SessionTracker] Network error: ${remaining_attempts} attempts remaining`);
      } else {
        logInfo('[SessionTracker] Network error: no more attempts');
      }
    });

    tracker.on('session_response', (response: { state: number }) => {
      const { state } = response;

      switch (state) {
        case SGXSessionTracker.States.SessionPending:
          logInfo('[SessionTracker] Session pending');
          break;

        case SGXSessionTracker.States.SessionStarted: {
          logInfo('[SessionTracker] Session started');
          const { cgxClient } = this;
          if (cgxClient !== null) {
            logInfo('[SessionTracker] > Starting client...');
            cgxClient.Start();
          } else {
            logError("[SessionTracker] > Cannot start client because it's null");
          }
          break;
        }

        case SGXSessionTracker.States.SessionCrashed:
          logInfo('[SessionTracker] Session crashed');
          break;

        case SGXSessionTracker.States.SessionFailedToStart:
          logInfo('[SessionTracker] Failed to start');
          break;

        case SGXSessionTracker.States.SessionConnectionLost:
        case SGXSessionTracker.States.SessionConnectTimeout:
        case SGXSessionTracker.States.SessionDurationLimitReached:
        case SGXSessionTracker.States.SessionInactiveTimeout:
        case SGXSessionTracker.States.SessionNotFound: // Should never happen
        case SGXSessionTracker.States.SessionReconnectTimeout:
        case SGXSessionTracker.States.SessionTerminated:
          logInfo(`[SessionTracker] Terminated | State: ${state}`);
          break;

        default:
          logInfo(`[SessionTracker] Unknown state: ${state}`);
          break;
      }
    });

    this.sessionTracker = tracker;

    // Start the tracker
    tracker.run();
  }

  runGame(session: GamestreamSession, gameConfig: GamestreamGameConfig) {
    const { useSsl } = session;

    if (GameManager.container === null) {
      logError('Missing client container');
      return;
    }

    const cgxClient = Player.instantiateClient(GameManager.container, gameConfig, useSsl ?? false, true);

    this.cgxClient = cgxClient;

    cgxClient.on('connected', () => this.handleConnected());
    cgxClient.on('disconnected', () => this.handleDisconnected(this));
    cgxClient.on('error', (errorMsg: string) => this.handleError(errorMsg));

    window.addEventListener('beforeunload', this.handleOnBeforeUnload, { passive: true });

    cgxClient.Setup();

    cgxClient.Resize(window.innerWidth, window.innerHeight);

    GameManager.CGXClientClass.resizeHandler = () => {
      cgxClient.Resize(window.innerWidth, window.innerHeight);
    };

    this.startSessionTracker(session);

    if (this.profileId !== null) {
      // Update playing history (locally and in personal data)
      fireAndForget(this.updatePlayingHistoryItem(this.profileId), 'GamestreamWrapper.updatePlayingHistoryItem');
    }
  }
}

export default GamestreamWrapper;
